/**
 * O vocabulário das automações, num lugar só.
 *
 * Gatilho e ação são listas fechadas, e o que cada uma significa não pode
 * divergir entre a demonstração e o servidor: é esse texto que a recepção lê
 * para decidir se liga ou não a regra.
 *
 * **Nada dispara sozinho nesta instalação.** Não há agendador, e o WhatsApp não
 * está pareado. Em vez de esconder isso atrás de um interruptor verde, cada
 * automação carrega o motivo pelo qual não executa — e as que dependem só do
 * banco executam de verdade, sob demanda.
 */

import type {
    AcaoDeAutomacao, Automacao, GatilhoDeAutomacao, Indicador,
} from './tipos';

export interface DescricaoDeGatilho {
    rotulo: string;
    /** O que a regra observa. */
    descricao: string;
    /** O que o número de alcance conta. Vazio para gatilho de evento. */
    alcanceRotulo: string;
    /** Evento não tem fila: dispara no instante em que a coisa acontece. */
    evento: boolean;
}

export const GATILHOS: Record<GatilhoDeAutomacao, DescricaoDeGatilho> = {
    'agendamento-criado': {
        rotulo: 'Agendamento criado',
        descricao: 'Assim que a recepção marca a consulta',
        alcanceRotulo: 'dispara no evento, sem fila',
        evento: true,
    },
    'agendamento-24h': {
        rotulo: '24h antes do atendimento',
        descricao: 'Consultas de amanhã que ainda não foram lembradas',
        alcanceRotulo: 'consultas nas próximas 24 h',
        evento: false,
    },
    'atendimento-concluido': {
        rotulo: 'Atendimento concluído',
        descricao: 'Quando a consulta é encerrada no editor',
        alcanceRotulo: 'dispara no evento, sem fila',
        evento: true,
    },
    'titulo-vencido': {
        rotulo: 'Título vencido há 3 dias',
        descricao: 'Contas a receber em atraso, ainda sem baixa',
        alcanceRotulo: 'títulos vencidos há 3 dias ou mais',
        evento: false,
    },
    'sem-retorno-90d': {
        rotulo: 'Paciente sem retorno há 90 dias',
        descricao: 'Quem foi atendido e não voltou nem tem consulta marcada',
        alcanceRotulo: 'pacientes sem retorno',
        evento: false,
    },
    'estoque-minimo': {
        rotulo: 'Insumo abaixo do mínimo',
        descricao: 'Produtos cujo saldo caiu abaixo do mínimo declarado',
        alcanceRotulo: 'produtos abaixo do mínimo',
        evento: false,
    },
};

export interface DescricaoDeAcao {
    rotulo: string;
    /** Ação interna escreve no próprio banco — e por isso pode rodar agora. */
    interna: boolean;
}

export const ACOES: Record<AcaoDeAutomacao, DescricaoDeAcao> = {
    'enviar-whatsapp': { rotulo: 'Enviar mensagem no WhatsApp', interna: false },
    'criar-tarefa': { rotulo: 'Criar tarefa para a equipe', interna: true },
    'adicionar-campanha': { rotulo: 'Adicionar à campanha de reativação', interna: false },
};

/** O motivo de a automação não poder executar; nulo quando pode. */
export function bloqueioDe(acao: AcaoDeAutomacao, canalConectado: boolean): string | undefined {
    if (acao === 'enviar-whatsapp') {
        /* Com canal pareado, quem manda é o agendador, a cada 5 minutos — desde
           que a clínica ligue o envio automático, o que a tela diz em cima. */
        return canalConectado
            ? undefined
            : 'Nenhum canal de WhatsApp está pareado — a mensagem não teria por onde sair.';
    }
    if (acao === 'adicionar-campanha') {
        return 'Adicionar à campanha não roda sozinho: o público da campanha é calculado e confirmado na hora do disparo, em Marketing.';
    }
    return undefined;
}

export interface LinhaDeAutomacao {
    id: string;
    nome: string;
    gatilho: GatilhoDeAutomacao;
    acao: AcaoDeAutomacao;
    ativa: boolean;
    execucoes: number;
    ultimaExecucaoEm?: string;
}

export function montarAutomacao(linha: LinhaDeAutomacao, alcance: number | undefined, canalConectado: boolean): Automacao {
    const gatilho = GATILHOS[linha.gatilho];
    const bloqueio = bloqueioDe(linha.acao, canalConectado);
    return {
        ...linha,
        ...(gatilho.evento || alcance === undefined ? {} : { alcance }),
        alcanceRotulo: gatilho.alcanceRotulo,
        /* Regra pausada não executa nem à mão: executar a pausada seria o
           interruptor não querer dizer nada. E "executar agora" é só para ação
           interna: a mensagem sai pelo agendador, e um botão que dissesse
           "executar" e não mandasse nada seria o defeito de antes. */
        executavel: !bloqueio && linha.ativa && !gatilho.evento && ACOES[linha.acao].interna,
        ...(bloqueio ? { bloqueio } : {}),
    };
}

export function indicadoresDeAutomacoes(automacoes: Automacao[]): Indicador[] {
    const ativas = automacoes.filter((a) => a.ativa);
    const executaveis = automacoes.filter((a) => a.executavel);
    const naFila = automacoes.reduce((s, a) => s + (a.executavel ? (a.alcance ?? 0) : 0), 0);
    const execucoes = automacoes.reduce((s, a) => s + a.execucoes, 0);

    return [
        { id: 'ativas', rotulo: 'Ativas', valor: String(ativas.length), apoio: `de ${automacoes.length} configuradas`, icone: 'workflow' },
        {
            id: 'executaveis', rotulo: 'Executáveis hoje', valor: String(executaveis.length),
            apoio: 'à mão só as de tarefa; as de mensagem saem pelo agendador',
            tendencia: executaveis.length ? 'up' : 'neutral', icone: 'play',
        },
        { id: 'fila', rotulo: 'Na fila agora', valor: String(naFila), apoio: 'registros alcançados pelas executáveis', icone: 'list-checks' },
        /* Execuções conta o que aconteceu de verdade. Zero é a resposta certa
           enquanto ninguém executou — e é a diferença entre esta tela e a do
           protótipo, que exibia 974. */
        { id: 'execucoes', rotulo: 'Execuções registradas', valor: String(execucoes), apoio: 'todas sob demanda', icone: 'history' },
    ];
}
